import type { ReactNode } from "react";
import { Alert, Button, Modal } from "@/ui";
import { ConfirmNote } from "./ConfirmNote";
import { useSave } from "./useSave";

interface Props<Out> {
  title: string;
  /** 确认按钮的文案与样式：危险操作用 danger。 */
  confirmLabel: string;
  danger?: boolean;
  /** 写清这次操作会发生什么、是否进审计。 */
  note: ReactNode;
  action: () => Promise<Out>;
  invalidate: string[];
  message: string;
  /** 成功后、关闭前把接口返回交给调用方（如价格重新同步的任务号）。 */
  onSaved?: (out: Out) => void;
  onClose: () => void;
  /** 说明下方的可选输入（如备注），状态由调用方持有并在 action 里使用。 */
  children?: ReactNode;
}

/** 单个操作的二次确认弹窗：确认后调接口，成功刷新并关闭；502 是已保存、后续同步未完成，只留"关闭"免得重复提交（决策 D57）。
 * 摘除或恢复通道、重置利润保护、成本价重新同步等共用。 */
export function ActionModal<Out>({ title, confirmLabel, danger, note, action, invalidate, message, onSaved, onClose, children }: Props<Out>) {
  const { save, saving, error, warning } = useSave(action, { invalidate, message, onSaved: (out) => { onSaved?.(out); onClose(); } });
  return (
    <Modal open title={title} onClose={onClose}
      footer={warning ? <Button onClick={onClose}>关闭</Button> : <><Button onClick={onClose}>取消</Button><Button variant={danger ? "danger" : "primary"} disabled={saving} onClick={() => save(undefined)}>{confirmLabel}</Button></>}>
      {error && <Alert tone="danger" title="操作失败">{error}</Alert>}
      {warning && <Alert tone="warning" title="已保存，后续同步未完成">{warning}</Alert>}
      <ConfirmNote>{note}</ConfirmNote>
      {children}
    </Modal>
  );
}
